import React, { useContext, useMemo, useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha, useTheme } from '@mui/material/styles';
import { Icon } from '@iconify/react';
import { useAccount, useSwitchChain } from 'wagmi';

import { AuthContext } from 'src/auth/context';
import { useEthereum } from 'src/hooks/useEthereum';
import { shortAddress } from 'src/utils/shortAddress';
import { getTargetName } from 'src/utils/crypto/networks';

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export default function ConnectWalletButton() {
  const theme = useTheme();
  const { logout } = useContext(AuthContext);
  const [anchorEl, setAnchorEl] = useState(null);

  const {
    connectWallet,
    disconnectWallet,
    connected,
    walletChainId,
    targetChainId,
    status,
    isConnecting,
    address,
  } = useEthereum();

  const { chain } = useAccount();
  const { switchChainAsync, isPending: isSwitching } = useSwitchChain();

  const open = Boolean(anchorEl);
  const connecting = status === 'connecting' || status === 'reconnecting' || isConnecting;

  const handleOpenMenu = (e) => setAnchorEl(e.currentTarget);
  const handleCloseMenu = useCallback(() => setAnchorEl(null), []);
  const requiredChainId = Number(targetChainId);
  const hasRequiredChain = Number.isFinite(requiredChainId) && requiredChainId > 0;

  const handlePrimaryAction = useCallback(async () => {
    try {
      if (connecting) return;

      if (!connected) {
        await connectWallet();
        return;
      }

      await disconnectWallet();
    } catch (error) {
      console.error('Wallet action failed:', error);
    }
  }, [connecting, connected, connectWallet, disconnectWallet]);

  const handleCopy = async () => {
    if (!address) return;
    await copyToClipboard(address);
    handleCloseMenu();
  };

  const handleDisconnect = async () => {
    try {
      await disconnectWallet();
    } catch (error) {
      console.error('Wallet disconnect failed:', error);
    } finally {
      handleCloseMenu();
    }
  };

  const handleSwitchNetwork = async () => {
    try {
      if (!hasRequiredChain) return;

      await switchChainAsync({ chainId: requiredChainId });
    } catch (error) {
      console.error('Switch network failed:', error);
    } finally {
      handleCloseMenu();
    }
  };

  const handleLogout = async () => {
    try {
      // End the server session first, then drop the wallet connection, so
      // nothing races the logout request.
      await logout();
      await disconnectWallet();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      handleCloseMenu();
    }
  };

  const targetName = useMemo(() => getTargetName(), []);
  const wrongNetwork =
    connected &&
    hasRequiredChain &&
    walletChainId != null &&
    Number(walletChainId) !== requiredChainId;

  function getButtonLabel() {
    if (connected && address) {
      return shortAddress(address);
    }

    if (connecting) {
      return 'Connecting…';
    }

    return 'Connect';
  }

  const buttonLabel = getButtonLabel();

  if (!connected) {
    return (
      <Stack
        direction="row"
        spacing={1}
        sx={{
          width: { xs: '100%', sm: 'auto' },
        }}
      >
        <Button
          variant="contained"
          size="small"
          disabled={connecting}
          onClick={handlePrimaryAction}
          startIcon={
            connecting ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <Icon icon="solar:wallet-bold" width={16} />
            )
          }
          sx={{
            flex: { xs: 1, sm: 'unset' },
            minWidth: 128,
            height: 40,
            borderRadius: 999,
            px: 2,
            textTransform: 'none',
            fontWeight: 800,
            boxShadow: 'none',
          }}
        >
          {buttonLabel}
        </Button>

        <Button
          variant="outlined"
          color="inherit"
          size="small"
          onClick={handleLogout}
          startIcon={<Icon icon="mdi:logout" width={16} />}
          sx={{
            height: 40,
            borderRadius: 999,
            px: 2,
            textTransform: 'none',
            fontWeight: 700,
            borderColor: alpha(theme.palette.text.primary, 0.16),
          }}
        >
          Logout
        </Button>
      </Stack>
    );
  }

  return (
    <>
      <Tooltip title={wrongNetwork ? `Switch to ${targetName}` : address || ''} arrow>
        <Button
          variant="outlined"
          size="small"
          onClick={handleOpenMenu}
          endIcon={<Icon icon="solar:alt-arrow-down-bold" width={16} />}
          sx={{
            height: 42,
            minWidth: wrongNetwork ? 190 : 156,
            maxWidth: 240,
            borderRadius: 999,
            px: 2,
            textTransform: 'none',
            fontWeight: 800,
            justifyContent: 'space-between',
            gap: 1,
            borderColor: wrongNetwork
              ? alpha(theme.palette.error.main, 0.45)
              : alpha(theme.palette.text.primary, 0.16),
            color: wrongNetwork ? theme.palette.error.main : theme.palette.text.primary,
            backgroundColor: wrongNetwork
              ? alpha(theme.palette.error.main, 0.05)
              : alpha(theme.palette.background.paper, 0.7),
            '&:hover': {
              borderColor: wrongNetwork
                ? alpha(theme.palette.error.main, 0.65)
                : alpha(theme.palette.text.primary, 0.3),
              backgroundColor: wrongNetwork
                ? alpha(theme.palette.error.main, 0.08)
                : alpha(theme.palette.action.hover, 0.8),
            },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                flexShrink: 0,
                bgcolor: wrongNetwork ? 'error.main' : 'success.main',
                boxShadow: `0 0 0 3px ${
                  wrongNetwork
                    ? alpha(theme.palette.error.main, 0.16)
                    : alpha(theme.palette.success.main, 0.16)
                }`,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 800,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {wrongNetwork ? `Wrong network • ${targetName}` : shortAddress(address ?? '')}
            </Typography>
          </Stack>
        </Button>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          elevation: 8,
          sx: {
            mt: 1,
            minWidth: 290,
            borderRadius: 3,
            overflow: 'hidden',
            border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
            boxShadow: `0 16px 40px ${alpha(theme.palette.common.black, 0.14)}`,
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.75,
            background: wrongNetwork
              ? `linear-gradient(180deg, ${alpha(theme.palette.error.main, 0.08)} 0%, transparent 100%)`
              : `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 100%)`,
          }}
        >
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={1.5}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                Connected wallet
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                  color: 'text.secondary',
                  wordBreak: 'break-all',
                  lineHeight: 1.4,
                }}
              >
                {address}
              </Typography>
            </Box>

            <Chip
              size="small"
              label={wrongNetwork ? 'Wrong network' : (chain?.name ?? 'Connected')}
              color={wrongNetwork ? 'error' : 'success'}
              variant={wrongNetwork ? 'filled' : 'outlined'}
              sx={{ fontWeight: 700 }}
            />
          </Stack>

          {wrongNetwork && (
            <Typography
              variant="caption"
              sx={{
                mt: 1.25,
                display: 'block',
                color: 'error.main',
                fontWeight: 700,
              }}
            >
              Please switch to {targetName} to continue.
            </Typography>
          )}
        </Box>

        <Divider />

        <MenuItem onClick={handleCopy} sx={{ gap: 1.25, py: 1.25 }}>
          <Icon icon="mdi:content-copy" width={18} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Copy address
          </Typography>
        </MenuItem>

        <MenuItem disabled sx={{ gap: 1.25, py: 1.25, opacity: 1 }}>
          <Icon icon="mdi:ethereum" width={16} />
          <Stack spacing={0.25}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Network
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {chain?.name ?? 'Unknown'}
            </Typography>
          </Stack>
        </MenuItem>

        <MenuItem
          onClick={handleSwitchNetwork}
          disabled={!wrongNetwork || isSwitching}
          sx={{ gap: 1.25, py: 1.25 }}
        >
          {isSwitching ? (
            <CircularProgress size={16} />
          ) : (
            <Icon icon="mdi:swap-horizontal" width={18} />
          )}

          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {isSwitching ? 'Switching network…' : `Switch to ${targetName}`}
          </Typography>
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleDisconnect} sx={{ gap: 1.25, py: 1.25 }}>
          <Icon icon="tabler:wallet-off" width={18} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Disconnect wallet
          </Typography>
        </MenuItem>

        <MenuItem
          onClick={handleLogout}
          sx={{
            gap: 1.25,
            py: 1.25,
            color: 'error.main',
          }}
        >
          <Icon icon="mdi:logout" width={18} />
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Logout
          </Typography>
        </MenuItem>
      </Menu>
    </>
  );
}
